import { useTheme } from '@mui/material/styles'
import * as React from 'react'
import { FC, useMemo, useEffect } from 'react'
import { Field, useField, useFormikContext } from 'formik'
import CollapsibleFilter from './atoms/CollapsibleFilter'
import { DateRange, IFilterProps } from '../typings'
import { isValidDate } from '@sales-center/common'
import {
  MuiDatePickerFormik,
  MuiTextFieldFormik,
} from '@user/components/Formik'

import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import MenuItem from '@mui/material/MenuItem'
import Typography from '@mui/material/Typography'
import { useLocalizationDateFormat } from '@user/features/Localization/useLocalizationDateFormat'

interface Props extends IFilterProps {
  disablePast?: boolean
  disableFuture?: boolean
}

export const DateRangeFilter: FC<Props> = ({
  name,
  label,
  reset,
  ...dateRangeProps
}) => {
  const path = `filters.${name}.value`
  const matchTypeField = `${path}.matchType`

  const [
    {
      value: { start, end, matchType },
    },
  ] = useField<DateRange>(path)

  const { setFieldValue } = useFormikContext()
  const { formatDate } = useLocalizationDateFormat()

  const subLabel = useMemo(() => {
    if (matchType === 'bt') {
      if (isValidDate(start) && isValidDate(end))
        return `From ${formatDate(start)} - To ${formatDate(end)}`
    } else if (isValidDate(start)) {
      return `${matchType === 'gt' ? 'After' : 'Before'} ${formatDate(start)}`
    }
  }, [end, start, matchType, formatDate])

  const theme = useTheme()

  useEffect(() => {
    if (matchType !== 'bt') setFieldValue(`${path}.end`, null)
  }, [matchType, path, setFieldValue])

  return (
    <CollapsibleFilter
      label={label}
      subLabel={(expanded) => !expanded && subLabel}
      reset={reset}
    >
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Box display="flex" alignItems="center">
            <Typography color="textSecondary">Value</Typography>
            <Field
              name={matchTypeField}
              component={MuiTextFieldFormik}
              select
              size="small"
              style={{ margin: `0 ${theme.spacing(1)}` }}
            >
              <MenuItem value="lt">Before</MenuItem>
              <MenuItem value="bt">Between</MenuItem>
              <MenuItem value="gt">After</MenuItem>
            </Field>
          </Box>
        </Grid>

        <Grid item xs>
          <Field
            name={`${path}.start`}
            component={MuiDatePickerFormik}
            fullWidth={matchType !== 'bt'}
            label={matchType === 'bt' ? 'From' : 'Date'}
            {...dateRangeProps}
          />
        </Grid>

        {matchType === 'bt' && (
          <Grid item xs>
            <Field
              name={`${path}.end`}
              component={MuiDatePickerFormik}
              label="To"
              clearable
              {...dateRangeProps}
            />
          </Grid>
        )}
      </Grid>
    </CollapsibleFilter>
  )
}
DateRangeFilter.displayName = 'DateRangeFilter'
